import { useState } from 'react';
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AnimatePresence, motion } from 'framer-motion';
import { faSparkles } from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import { LearnMoreReferrals } from '@components/Referrals/LearnMore';
import { ShareReleaseImage } from '@components/Share/ShareReleaseImage';
import { Input } from '@components/basics/forms/inputs/Input';
import { HeyLogo } from '@components/icons/Social/Hey';
import { TelegramLogo } from '@components/icons/Social/Telegram';
import { TwitterLogo } from '@components/icons/Social/Twitter';
import { WarpcastLogo } from '@components/icons/Social/Warpcast';
import { Button } from '@components/main-components/button/Button';
import { StandardModal } from '@components/main-components/modals/StandardModal';
import { TabList, Tabs, TabTrigger } from '@components/main-components/tabs/Tab';
import { useAuth } from '@context/auth/base';
import { triggerLogin } from '@context/auth/store';
import { ModalContext } from '@context/modal';
import { ToastContext } from '@context/toast';
import type { FragmentType } from '@graphql/reactQuery';
import {
  getFragment,
  ReleaseShareInfoDocument,
  ReleaseShareInfoFragmentDoc,
  useQuery,
} from '@graphql/reactQuery';
import { useCheckReferrableRelease } from '@hooks/useCheckReferrable';
import { usePrefetchEmbed } from '@hooks/usePrefetchEmbed';
import { useUrlWithAuthReferral } from '@hooks/useUrlWithReferral';
import { trackClickEvent } from '@lib/analytics';
import { EmbedBody } from './EmbedBody';
import { SocialShare } from './SocialShare';
import { UrlCopy } from './UrlCopy';
import {
  AnimationVariants,
  SHARE_TRANSITION,
  ShareTab,
  type ShareTabKey,
  sourceLink,
} from './helpers';

export function ShareModalRelease({ releaseId }: { releaseId: string }) {
  const { data } = useQuery(ReleaseShareInfoDocument, {
    variables: {
      id: releaseId,
    },
    staleTime: 0,
  });

  const { closeCancelModal } = ModalContext.useContainer();

  const release = data?.data.release;

  if (!release) {
    return (
      <StandardModal
        body={loadingBody}
        title="Share"
        closeModal={closeCancelModal}
        bodyWithoutPaddingBottom
        mobileFullScreen
        bodyClassName="pt-4"
      />
    );
  }

  return <ShareModalReleaseInternal release={release} />;
}

function ShareModalReleaseInternal({
  release,
}: {
  release: FragmentType<ReleaseShareInfoFragmentDoc> & { id: string };
}) {
  const { isAuthenticatedWithAPI } = useAuth();
  const { closeCancelModal } = ModalContext.useContainer();
  const { openToast } = ToastContext.useContainer();
  const [activeTab, setActiveTab] = useState<ShareTabKey>('social');
  const [direction, setDirection] = useState(1);

  const releaseInfo = getFragment(ReleaseShareInfoFragmentDoc, release);

  const { isShareReferrable } = useCheckReferrableRelease({
    release: releaseInfo,
  });

  const { urlWithReferral, copyUrlWithReferral, isCopied, referralAddress } =
    useUrlWithAuthReferral({
      url: releaseInfo.webappUri,
      source: sourceLink,
      isReferrable: isShareReferrable,
    });

  usePrefetchEmbed({
    releaseId: releaseInfo.id,
  });

  const onConnectClick = () => {
    trackClickEvent({
      event: 'Sign in Clicked',
      properties: { sourceComponent: 'Share Release Modal' },
    });
    closeCancelModal();
    triggerLogin();
  };

  const socialBody = (
    <div className="flex flex-col gap-6 pt-3">
      {isShareReferrable && (
        <div className="flex justify-between rounded-md bg-base50 p-4">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1 font-title text-title-xs font-medium text-base800">
              <FontAwesomeIcon icon={faSparkles} />
              <span>Earn Curator Rewards</span>
            </div>
            <div className="text-base-s text-base600">
              For each primary sale from your link.
              {isAuthenticatedWithAPI && (
                <>
                  {' '}
                  <LearnMoreReferrals textColor="black" />
                </>
              )}
            </div>
          </div>
          {!isAuthenticatedWithAPI && (
            <Button
              label="Sign in"
              variant="tertiary"
              className="h-9 w-[78px] p-2"
              onClick={onConnectClick}
            />
          )}
        </div>
      )}
      <SocialShare
        url={urlWithReferral}
        associationType="Release"
        messageType="drop"
        referralAddress={referralAddress}
        id={releaseInfo.id}
      />
      <UrlCopy url={urlWithReferral} isCopied={isCopied} onCopyClick={copyUrlWithReferral} />
    </div>
  );

  const imageBody = (
    <ShareReleaseImage
      releaseId={releaseInfo.id}
      // TODO: linear.app/sound/issue/ENG-9185/add-missing-observability-in-fe
      onError={() =>
        openToast({
          text: 'There was an error downloading release screenshot',
          variant: 'error',
        })
      }
      className="col-span-3 mb-0.5 pt-3"
      fullWidthButton
    />
  );

  const embedBody = <EmbedBody type="drop" className="mb-0.5 pt-3" releaseId={releaseInfo.id} />;

  const onTabChange = (tab: ShareTabKey) => {
    const direction = ShareTab[activeTab].position < ShareTab[tab].position ? 1 : -1;
    setDirection(direction);
    setActiveTab(tab);
  };

  const body = (
    <AnimatePresence initial={false} mode="popLayout" custom={direction}>
      <Tabs
        defaultValue={ShareTab.social.key}
        value={activeTab}
        onValueChange={v => onTabChange(v as ShareTabKey)}
      >
        <TabList>
          <TabTrigger value={ShareTab.social.key} label="Social Share" className="normal-case" />
          <TabTrigger value={ShareTab.image.key} label="Share Image" className="normal-case" />
          <TabTrigger value={ShareTab.embed.key} label="Embed" className="normal-case" />
        </TabList>
      </Tabs>
      <motion.div
        variants={AnimationVariants}
        initial="enter"
        animate="center"
        exit="exit"
        transition={SHARE_TRANSITION}
        key={activeTab}
        custom={direction}
      >
        {activeTab === ShareTab.social.key && socialBody}
        {activeTab === ShareTab.image.key && imageBody}
        {activeTab === ShareTab.embed.key && embedBody}
      </motion.div>
    </AnimatePresence>
  );

  return (
    <StandardModal
      body={body}
      title="Share"
      closeModal={closeCancelModal}
      bodyClassName="pt-4 overflow-x-hidden"
      mobileFullScreen
      bodyWithoutPaddingBottom
    />
  );
}

const loadingBody = (
  <div>
    <div className="h-[33px]" />
    <div className="flex animate-pulse flex-col gap-6 pt-3">
      <div className="h-[70px] w-full rounded-md bg-base50" />
      <div className="mx-auto flex gap-4">
        <TwitterLogo />
        <WarpcastLogo />
        <HeyLogo />
        <TelegramLogo />
      </div>
      <div className="mb-0.5 flex h-10 w-full gap-2">
        <Input disabled className="truncate !text-black" />
        <div className="w-[78px]">
          <Button variant="primary" label="Copy" disabled className="h-full w-full min-w-0" />
        </div>
      </div>
    </div>
  </div>
);
